// AutomationEditor.js
// One Automation, whole: WHAT it runs (a Function, pinned to one published
// version), WHEN it runs (a schedule, or new data landing in a table it reads),
// and WHAT IT MAY DO (email, or not).
//
// Every derived surface here — the parameters offered, the watched-table
// options, the email recipients named in the consent dialog — comes from the
// PUBLISHED version this automation is pinned to, never from a draft. That is
// the point of moving this out of the Function builder: the builder edits a
// draft, so anything it described about an automation described something that
// was not going to run.
//
// EDIT → LEASE → SAVE: the document arrives live (the Dashboard watches the
// whole trigger collection while the tab is open) and is shown READ-ONLY —
// every configuration control genuinely disabled, mouse and keyboard — until
// Edit is pressed. Edit takes the document's edit lease (the one the builders
// use), copies the configuration into a local draft, and from then on nothing
// is written until Save, which is one transaction that requires the lease to
// still be this session's and writes only the draft's own fields. A change
// arriving from the dispatcher or another session meanwhile — a run receipt,
// an automatic switch-off, a takeover — still shows, because the display reads
// the live document for everything the draft does not own. Cancel discards.
//
// The three actions that are not configuration — the on/off switch, the Email
// permission, Delete — stay immediate in read mode: each is its own
// transaction with its own confirmation. They are unavailable while editing.
//
// THE PIN: an enabled automation always executes the version its Function
// publishes now. Publishing a new version switches user-created automations off;
// enabling one here re-pins it and migrates its saved filters onto the new
// contract, dropping any the new version no longer understands. Every
// configuration save also switches Email off — see THE INVARIANT in
// services/functionTriggers.js — so Email is always a separate, explicitly
// confirmed step.
//
// A View-managed automation belongs to a card of its View and never emails.
// Only its on/off switch is offered here; its schedule and parameters are
// configured in the View's "Keep current" dialog, which renders the same
// AutomationConfigForm this editor does.

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  FormControlLabel,
  IconButton,
  Paper,
  Stack,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import { doc } from 'firebase/firestore';
import { db } from '../../services/firebase';
import useEditLease from '../../hooks/useEditLease';
import { leaseStartedLabel } from '../../services/editLease';
import LoadFailure from '../common/LoadFailure';
import { INCOMPLETE_DATES_MESSAGE } from '../common/DateDraftScope';
import AutomationConfigForm from './AutomationConfigForm';
import { prepareRunParameters, formatParameterErrors } from '../../utils/functionParameterUtils';
import { formatText } from '../common/TextFormatterProvider';
import {
  MAX_RUN_FAILURES,
  automationDraftOf,
  automationConfigSnapshot,
  commitTriggerConfig,
  deleteTrigger,
  describeWhen,
  enableAutomation,
  migrateTriggerParameters,
  sameAutomationConfig,
  saveAutomationDraft,
  setAutomationEmailPermission,
} from '../../services/functionTriggers';
import { list as listContacts } from '../../services/emailContacts';
import { blockedRecipients, confirmationChanged, recipientEntry } from '../../utils/emailConfirmation';
import {
  isPublishedVersion,
  loadFunctionVersion,
  versionDataSourceBlocks,
  versionEmailOutputs,
} from '../../services/functionVersions';
import { outcomeDisplay } from '../../utils/runDisplay';

const MANAGED_HELPER = 'Managed by its View. Configure or remove it from the View\'s Keep current dialog.';

// The filters a migration reset, named (H85). Empty when every saved filter
// survived.
const resetSentence = (dropped, future = false) => {
  const columns = Array.from(new Set((dropped || []).map((entry) => entry.column))).sort();
  if (columns.length === 0) return '';
  const one = columns.length === 1;
  const names = columns.map((column) => column.replace(/_/g, ' ')).join(', ');
  return ` The saved filter${one ? '' : 's'} ${names} no longer appl${one ? 'ies' : 'y'} and ${future ? 'will be' : (one ? 'was' : 'were')} removed.`;
};

// Every Email Output's recipients as `contactList` has them: "Name <address>"
// and, for a contact that cannot be emailed (suppressed, deleted, still being
// restored, or no longer in the contact book), the reason. `outputs` are
// `{ blockId, name, recipientContactIds }`; an output that was described before
// (it already has `recipients`) is described afresh from `contactList`.
const describeRecipients = (outputs, contactList) => outputs.map((output) => ({
  ...output,
  recipients: output.recipientContactIds.map((contactId) => recipientEntry(
    contactId,
    (contactList || []).find((contact) => (contact.id || contact.contact_id) === contactId),
  )),
}));

// The data-source blocks of the version an automation is pinned to, for the
// type comparison of a migration. Best effort, as in the publish dialog: an
// unreadable version only costs the type check.
const loadPinnedBlocks = async (companyName, automation) => {
  if (!isPublishedVersion(automation?.function_version)) return null;
  try {
    const content = await loadFunctionVersion(companyName, automation.function_id, automation.function_version);
    return content ? versionDataSourceBlocks(content) : null;
  } catch (err) {
    console.error('Failed to read the version this automation was pinned to:', err);
    return null;
  }
};

function AutomationEditor({
  companyName,
  automation,
  // The `loadRunTarget` read of this automation's Function: its head and the
  // content of the version it publishes now. Three things are told apart: the
  // read still running (`targetLoading`), the read having failed
  // (`targetError`), and the read answering that the function is gone (a null
  // target with neither flag) — only the last recommends deletion.
  target,
  targetError = false,
  targetLoading = false,
  onRetryTarget,
  tables = [],
  contacts = [],
  actorUid,
  actorName = 'Another user',
  // Called after every committed write so the list beside this editor and the
  // automation prop it feeds back in are re-read from Firestore.
  onChanged,
  onDeleted,
}) {
  // `automation` is the live document. `draft` exists only while this session
  // holds the edit lease and is what the configuration controls edit.
  //
  // An editing session is one press of Edit: it mints the lease identity and
  // drops it when the session ends, so a session that was taken over can never
  // come back under the identity the lease still names — saveAutomationDraft
  // authorises a write by the session id alone.
  const [editSession, setEditSession] = useState(null);
  const editing = editSession !== null;
  const startEditing = useCallback(() => setEditSession({ sessionId: crypto.randomUUID() }), []);
  const stopEditing = useCallback(() => setEditSession(null), []);
  const [draft, setDraft] = useState(null);
  const [error, setError] = useState('');
  // Why an editing session ended without a save — a takeover, a deletion.
  const [notice, setNotice] = useState('');
  const [busy, setBusy] = useState(false);
  // Half-typed dates (see AppDatePicker): Save refuses while any picker in
  // this scope is partial, and reveals them.
  const dateScopeRef = useRef(null);
  const [emailReview, setEmailReview] = useState(null);
  const [migrateConfirmOpen, setMigrateConfirmOpen] = useState(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);

  const leaseRef = useMemo(() => (
    companyName && automation?.id
      ? doc(db, `users/${companyName}/function_triggers`, automation.id)
      : null
  ), [companyName, automation?.id]);
  const lease = useEditLease({
    ref: leaseRef,
    uid: actorUid,
    holderName: actorName,
    noun: 'automation',
    sessionId: editSession?.sessionId || null,
  });
  const held = lease.state === 'held';

  // The draft is copied from the live document at the moment the lease is
  // granted — not when Edit was pressed, in case the document moved meanwhile.
  const liveRef = useRef(automation);
  liveRef.current = automation;

  // An editing session that ended without a save: the lease was taken over
  // (seen by the heartbeat, or by the live document carrying someone else's
  // lease) or the document was deleted. The draft is discarded and the reader
  // is told why.
  useEffect(() => {
    if (lease.state !== 'lost' && lease.state !== 'deleted') return;
    setNotice(lease.state === 'deleted'
      ? 'This automation was deleted while you were editing it. Changes you had not saved were not kept.'
      : 'Another session took over editing this automation, so your unsaved changes were not kept.');
    stopEditing();
    setDraft(null);
  }, [lease.state, stopEditing]);
  // A foreign lease on the live document is a takeover only once this session's
  // own lease has been seen there: the lease this session replaced (an expired
  // one, or one it took over) stays on the snapshot for the moment before our
  // own write arrives, and must not read as someone taking over from us. The
  // two leases are written by different machines, so their timestamps cannot
  // decide this — what the live document has already shown can.
  const liveLease = automation?.edit_lease || null;
  const liveLeaseSession = liveLease?.session_id || null;
  const { sessionId: leaseSessionId, markLost: markLeaseLost } = lease;
  const leaseSeenOnLiveDocRef = useRef(null);
  useEffect(() => {
    if (!held) return;
    if (liveLeaseSession === leaseSessionId) {
      leaseSeenOnLiveDocRef.current = leaseSessionId;
      return;
    }
    // A lease cleared entirely is this session's own save, which releases it in
    // the same write, not someone taking over.
    if (!liveLeaseSession) return;
    if (leaseSeenOnLiveDocRef.current !== leaseSessionId) return;
    markLeaseLost();
  }, [held, liveLeaseSession, leaseSessionId, markLeaseLost]);

  const managed = Boolean(automation?.managed_by);
  const publishedVersion = target?.publishedVersion || null;
  const versionAvailable = Boolean(target?.content);
  const pinMatchesHead = Number.isInteger(automation?.function_version)
    && automation.function_version === publishedVersion;

  // Editing and saving both read the published version's filters: the draft is
  // seeded against them and Save validates against them. Neither is offered
  // until that version is in hand, and this says why ('' when it is).
  let versionBlockedReason = '';
  if (targetLoading) {
    versionBlockedReason = "Loading this function's published version…";
  } else if (targetError) {
    versionBlockedReason = "This function's published version could not be loaded.";
  } else if (!target) {
    versionBlockedReason = "This automation's function no longer exists.";
  } else if (!versionAvailable) {
    versionBlockedReason = 'This function has no published version.';
  }

  // What the configuration controls show: the draft while editing, else the
  // live document.
  const shown = held && draft ? { ...automation, ...draft } : automation;
  const dirty = Boolean(held && draft) && (
    draft.name !== (automation?.name || '')
    || !sameAutomationConfig(automationDraftOf(automation), draft)
    || (isPublishedVersion(draft.function_version) && draft.function_version !== automation?.function_version)
  );

  const dataSourceBlocks = useMemo(
    () => versionDataSourceBlocks(target?.content),
    [target],
  );

  const emailOutputs = useMemo(
    () => versionEmailOutputs(target?.content),
    [target],
  );

  // The draft edits against the version the Function publishes NOW — the one
  // the controls are built from. An automation pinned to an older version has
  // its saved filters carried onto this one before editing starts, and the
  // draft carries the new pin, so what is shown, what Save writes, and what
  // the pin says are one version; enabling it afterwards migrates nothing
  // (H85). The values the migration reset are named at once.
  const editTargetRef = useRef({ companyName, publishedVersion, dataSourceBlocks, versionAvailable });
  editTargetRef.current = { companyName, publishedVersion, dataSourceBlocks, versionAvailable };
  useEffect(() => {
    if (!held) return undefined;
    let cancelled = false;
    const live = liveRef.current;
    const editTarget = editTargetRef.current;
    const stale = editTarget.versionAvailable
      && isPublishedVersion(editTarget.publishedVersion)
      && live?.function_version !== editTarget.publishedVersion;
    const seed = async () => {
      let draft = automationDraftOf(live);
      let dropped = [];
      if (stale) {
        const oldBlocks = await loadPinnedBlocks(editTarget.companyName, live);
        const migrated = migrateTriggerParameters(live?.parameters, editTarget.dataSourceBlocks, oldBlocks);
        draft = { ...draft, parameters: migrated.parameters, function_version: editTarget.publishedVersion };
        dropped = migrated.dropped;
      }
      if (cancelled) return;
      setDraft((prev) => prev || draft);
      if (stale) {
        setNotice(`Editing against version ${editTarget.publishedVersion}, which this function publishes now.${resetSentence(dropped)} Save keeps this automation on version ${editTarget.publishedVersion}.`);
      }
    };
    seed();
    return () => { cancelled = true; };
  }, [held]);

  const tableNameById = useMemo(() => (tables || []).reduce((names, table) => (
    table?.id ? { ...names, [table.id]: formatText(table.name || table.id) } : names
  ), {}), [tables]);


  // --- Edit / Save / Cancel -------------------------------------------------

  const handleEdit = () => {
    setError('');
    setNotice('');
    startEditing();
  };

  const handleCancel = useCallback(() => {
    stopEditing();
    setDraft(null);
    setError('');
  }, [stopEditing]);

  const handleSave = async () => {
    if (!draft) return;
    // The version can leave mid-edit: selecting this automation again re-reads
    // it, and validating the draft against no filters would call every saved
    // source missing. The draft is kept; Save returns once the version has.
    if (versionBlockedReason) {
      setError(versionBlockedReason);
      return;
    }
    if (dateScopeRef.current?.reveal()) {
      setError(INCOMPLETE_DATES_MESSAGE);
      return;
    }
    const prepared = prepareRunParameters(draft.parameters, dataSourceBlocks, { allowDynamicDates: true });
    if (prepared.errors.length > 0) {
      setError(formatParameterErrors(prepared.errors, dataSourceBlocks));
      return;
    }
    setBusy(true);
    setError('');
    try {
      await saveAutomationDraft(companyName, automation.id, { ...draft, parameters: prepared.parameters }, {
        sessionId: lease.sessionId,
        actorUid,
      });
      // The save released the lease in its own write.
      lease.forget();
      stopEditing();
      setDraft(null);
      if (onChanged) await onChanged();
    } catch (err) {
      console.error('Failed to save this automation:', err);
      if (err.code === 'lease_lost' || err.code === 'automation_missing') {
        lease.markLost();
      } else {
        setError(`Failed to save this automation: ${err.message}`);
      }
    } finally {
      setBusy(false);
    }
  };

  // --- The immediate actions: on/off, email, delete -------------------------

  const handleEnable = async () => {
    setBusy(true);
    setError('');
    try {
      const result = await enableAutomation(companyName, automation, target, actorUid);
      if (result?.migrated) {
        setNotice(`Switched on against version ${result.version}.${resetSentence(result.dropped)}`);
      }
      if (onChanged) await onChanged();
    } catch (err) {
      console.error('Failed to enable this automation:', err);
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  // Switching off is a configuration change in THE INVARIANT's sense, so it
  // goes through the joint commit: Email is off again when it next runs.
  const handleDisable = async () => {
    setBusy(true);
    setError('');
    try {
      await commitTriggerConfig(companyName, automation.id, { enabled: false }, actorUid);
      if (onChanged) await onChanged();
    } catch (err) {
      console.error('Failed to switch this automation off:', err);
      setError(`Failed to switch this automation off: ${err.message}`);
    } finally {
      setBusy(false);
    }
  };

  // What enabling a stale-pinned automation would reset, named before the
  // person confirms (H85). null while the pinned version is being read.
  const [migratePreview, setMigratePreview] = useState(null);
  const previewMigration = async () => {
    const live = liveRef.current;
    const oldBlocks = await loadPinnedBlocks(companyName, live);
    setMigratePreview(migrateTriggerParameters(live?.parameters, dataSourceBlocks, oldBlocks).dropped);
  };

  const handleToggleEnabled = (next) => {
    if (!next) {
      handleDisable();
      return;
    }
    // A stale pin means the saved filters were written against a version that is
    // no longer what runs, so enabling rewrites them.
    if (!pinMatchesHead) {
      setMigratePreview(null);
      setMigrateConfirmOpen(true);
      previewMigration();
      return;
    }
    handleEnable();
  };

  // Explicit Email permission action, and the ONE write that is not a
  // configuration change: enabling requires the confirmation below, disabling is
  // immediate. Both write the canonical audit fields alongside.
  const writeAllowEmail = async (value) => {
    setBusy(true);
    setError('');
    try {
      await setAutomationEmailPermission(companyName, automation.id, value, {
        actorUid,
        expectedVersion: emailReview?.version,
        reviewedConfig: emailReview?.config,
      });
      setEmailReview(null);
      if (onChanged) await onChanged();
    } catch (err) {
      console.error('Failed to update the email permission:', err);
      setError(`Failed to update the email permission: ${err.message}`);
      if (['automation_changed', 'version_moved', 'function_invalid', 'function_missing', 'automation_missing'].includes(err.code)) {
        setEmailReview(null);
        if (onRetryTarget) await onRetryTarget();
      }
    } finally {
      setBusy(false);
    }
  };

  const handleDelete = async () => {
    setBusy(true);
    setError('');
    try {
      await deleteTrigger(companyName, automation.id);
      setDeleteConfirmOpen(false);
      if (onDeleted) await onDeleted(automation.id);
    } catch (err) {
      console.error('Failed to delete this automation:', err);
      setError(`Failed to delete this automation: ${err.message}`);
    } finally {
      setBusy(false);
    }
  };

  // The published version's Email Outputs with their recipients as the contact
  // book this tab loaded has them. Consent is to an address, so each recipient
  // reads "Name <address>" (H49), and one that cannot be emailed blocks it.
  const reviewableOutputs = useMemo(() => emailOutputs.map((output) => ({
    blockId: output.blockId,
    name: formatText(output.nodeName),
    recipientContactIds: output.recipientContactIds,
  })), [emailOutputs]);
  const outputsWithRecipients = useMemo(
    () => describeRecipients(reviewableOutputs, contacts),
    [reviewableOutputs, contacts],
  );
  const blockedNow = blockedRecipients({ outputs: outputsWithRecipients });
  // A recipient the contact book holds but cannot email (suppressed, deleted,
  // still being restored) is named. One it does not hold at all may only mean
  // the contact book has not loaded, so that case keeps the "could not be
  // read" wording below.
  const knownContactIds = new Set((contacts || []).map((contact) => contact.id || contact.contact_id));
  const blockedKnownRecipients = blockedNow.filter((recipient) => knownContactIds.has(recipient.id));
  const blockedRecipientsSentence = blockedKnownRecipients
    .map((recipient) => `${recipient.label}: ${recipient.blocked}`)
    .join(' ');

  const recipientsAvailable = outputsWithRecipients.length > 0
    && outputsWithRecipients.every((output) => output.recipients.length > 0)
    && blockedNow.length === 0;
  const canAllowEmail = automation?.enabled === true && pinMatchesHead && versionAvailable
    && !targetLoading && !targetError && !target?.invalid && recipientsAvailable;

  // The contact book this tab holds was read when the tab opened, so consent is
  // reviewed — and confirmed, below — against a fresh read of it.
  const openEmailReview = async () => {
    if (!canAllowEmail) return;
    setError('');
    setBusy(true);
    try {
      const freshContacts = await listContacts(companyName);
      setEmailReview({
        version: publishedVersion,
        config: automationConfigSnapshot(automation, { includeName: false }),
        outputs: describeRecipients(reviewableOutputs, freshContacts),
        changed: false,
      });
    } catch (err) {
      console.error('Failed to read the email recipients:', err);
      setError('The recipients could not be read. Try again.');
    } finally {
      setBusy(false);
    }
  };

  // Allow Email is written only if the recipients still read exactly as they
  // were reviewed and every one of them can be emailed. Otherwise the dialog
  // stays open on the updated recipients for another look.
  const handleConfirmAllowEmail = async () => {
    setError('');
    setBusy(true);
    let freshOutputs;
    try {
      freshOutputs = describeRecipients(emailReview.outputs, await listContacts(companyName));
    } catch (err) {
      console.error('Failed to re-check the email recipients:', err);
      setError('The recipients could not be re-checked. Try again.');
      setBusy(false);
      return;
    }
    const fresh = { outputs: freshOutputs };
    const recipientsChanged = confirmationChanged(emailReview, fresh);
    if (recipientsChanged || blockedRecipients(fresh).length > 0) {
      setEmailReview({ ...emailReview, outputs: freshOutputs, changed: recipientsChanged });
      setBusy(false);
      return;
    }
    await writeAllowEmail(true);
  };

  if (!automation) return null;

  const lastRun = automation.last_run_outcome || automation.last_run_status || null;
  const lastRunDisplay = lastRun ? outcomeDisplay(lastRun) : null;
  // Runs that started and failed since the last one that did not; the backend
  // switches the automation off when this reaches MAX_RUN_FAILURES.
  const consecutiveRunFailures = automation.run_failures;
  // Configuration is editable only while this session holds the lease; the
  // immediate actions are available only while it does not.
  const editable = held && !busy;
  const acting = busy || editing;

  return (
    <Paper sx={{
      p: 2.5,
      height: '100%',
      borderRadius: 1.5,
      border: (theme) => `1px solid ${theme.palette.divider}`,
      display: 'flex',
      flexDirection: 'column',
      overflow: 'auto',
      boxShadow: 1,
    }}>
      <Stack direction="row" alignItems="center" spacing={1}>
        {held && draft ? (
          <TextField
            variant="standard"
            fullWidth
            value={draft.name}
            disabled={busy}
            onChange={(e) => setDraft((prev) => (prev ? { ...prev, name: e.target.value } : prev))}
            inputProps={{ 'aria-label': 'Automation name', style: { fontSize: '1.15rem', fontWeight: 500 } }}
            sx={{ flex: 1, minWidth: 0 }}
          />
        ) : (
          <Typography variant="h6" sx={{ flex: 1, minWidth: 0 }} noWrap>{automation.name || automation.id}</Typography>
        )}
        {lastRunDisplay && (
          <Tooltip title={consecutiveRunFailures > 0
            ? `${consecutiveRunFailures} consecutive failed ${consecutiveRunFailures === 1 ? 'run' : 'runs'}. This automation is switched off after ${MAX_RUN_FAILURES}.`
            : ''}>
            <Chip
              size="small"
              variant="outlined"
              color={lastRunDisplay.chipColor}
              label={consecutiveRunFailures > 0
                ? `Last run: ${lastRunDisplay.label} · ${consecutiveRunFailures} of ${MAX_RUN_FAILURES}`
                : `Last run: ${lastRunDisplay.label}`}
            />
          </Tooltip>
        )}
        {managed && <Chip size="small" label="View-managed" />}
        <Tooltip title={automation.enabled ? 'Switch this automation off' : 'Switch this automation on'}>
          <span>
            <Switch
              checked={automation.enabled === true}
              disabled={acting || (!automation.enabled && !versionAvailable)}
              onChange={(e) => handleToggleEnabled(e.target.checked)}
              inputProps={{ 'aria-label': 'Automation enabled' }}
            />
          </span>
        </Tooltip>
        <Tooltip title={managed ? MANAGED_HELPER : 'Delete this automation'}>
          <span>
            <IconButton size="small" disabled={managed || acting} onClick={() => setDeleteConfirmOpen(true)}>
              <DeleteIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
      </Stack>

      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
        {`Runs ${formatText(target?.head?.name || automation.function_name || automation.function_id)}`}
        {publishedVersion ? ` · version ${publishedVersion}` : ''}
        {` · ${describeWhen(shown, tableNameById)}`}
      </Typography>

      {managed && (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
          {MANAGED_HELPER}
        </Typography>
      )}

      {/* Edit / Save / Cancel. A managed automation has nothing to edit here. */}
      {!managed && (
        <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 1.5 }}>
          {editing ? (
            <>
              <Button
                size="small"
                variant="contained"
                onClick={handleSave}
                disabled={!editable || !dirty || Boolean(versionBlockedReason)}
              >
                {busy ? 'Saving…' : 'Save'}
              </Button>
              <Button size="small" color="inherit" onClick={handleCancel} disabled={busy}>
                Cancel
              </Button>
              {lease.state === 'acquiring' && (
                <Typography variant="caption" color="text.secondary">Opening for editing…</Typography>
              )}
              {held && !dirty && !versionBlockedReason && (
                <Typography variant="caption" color="text.secondary">No changes yet.</Typography>
              )}
              {held && versionBlockedReason && (
                <Typography variant="caption" color="text.secondary">{versionBlockedReason}</Typography>
              )}
            </>
          ) : (
            <Tooltip title={versionBlockedReason}>
              <span>
                <Button
                  size="small"
                  variant="outlined"
                  color="inherit"
                  startIcon={<EditIcon fontSize="small" />}
                  onClick={handleEdit}
                  disabled={busy || Boolean(versionBlockedReason)}
                >
                  Edit
                </Button>
              </span>
            </Tooltip>
          )}
        </Stack>
      )}

      {notice && <Alert severity="info" sx={{ mt: 1.5 }} onClose={() => setNotice('')}>{notice}</Alert>}
      {error && <Alert severity="error" sx={{ mt: 1.5 }} onClose={() => setError('')}>{error}</Alert>}

      {!target && targetError && (
        <LoadFailure thing="this function's published version" onRetry={onRetryTarget} align="left" sx={{ p: 0, mt: 1.5 }} />
      )}
      {!target && !targetError && targetLoading && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1.5 }}>
          <CircularProgress size={14} />
          <Typography variant="body2" color="text.secondary">Loading this function&apos;s published version…</Typography>
        </Box>
      )}
      {!target && !targetError && !targetLoading && (
        <Alert severity="warning" sx={{ mt: 1.5 }}>
          This automation&apos;s function no longer exists. Delete the automation, or restore the function.
        </Alert>
      )}
      {target && !versionAvailable && (
        <Alert severity="warning" sx={{ mt: 1.5 }}>
          This function has no published version to run. Publish it before switching this automation on.
        </Alert>
      )}
      {target?.invalid && (
        <Alert severity="warning" sx={{ mt: 1.5 }}>
          {target.invalidReason || 'This function references a resource that no longer exists.'}
        </Alert>
      )}
      {!automation.enabled && automation.disabled_reason && (
        <Alert severity="info" sx={{ mt: 1.5 }}>{automation.disabled_reason}</Alert>
      )}
      {automation.enabled && versionAvailable && !pinMatchesHead && (
        <Alert severity="warning" sx={{ mt: 1.5 }}>
          {`This automation is configured against a different version from the one this function publishes now (version ${publishedVersion}). Switch it off and on again to update it.`}
        </Alert>
      )}

      <Divider sx={{ my: 2 }} />

      <AutomationConfigForm
        triggerType={automation.trigger_type}
        config={shown}
        onChange={(patch) => setDraft((prev) => (prev ? { ...prev, ...patch } : prev))}
        dataSourceBlocks={dataSourceBlocks}
        tables={tables}
        publishedVersion={publishedVersion}
        editable={editable}
        dateScopeRef={dateScopeRef}
      />

      {(emailOutputs.length > 0 || automation.allow_email === true) && (
        <>
          <Divider sx={{ my: 2 }} />
          <Typography variant="subtitle2" fontWeight={600}>Email</Typography>
          <FormControlLabel
            sx={{ ml: 0, mt: 0.5 }}
            control={(
              <Switch
                size="small"
                checked={automation.allow_email === true}
                disabled={managed || acting || (automation.allow_email !== true && !canAllowEmail)}
                onChange={(e) => (e.target.checked ? openEmailReview() : writeAllowEmail(false))}
              />
            )}
            label={<Typography variant="body2">Allow Email Outputs on each run</Typography>}
          />
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
            {managed
              ? MANAGED_HELPER
              : !automation.enabled
                ? 'Switch this automation on before allowing Email.'
                : !pinMatchesHead
                  ? 'Reload the Function and update this automation to its published version before allowing Email.'
                  : blockedRecipientsSentence
                    ? blockedRecipientsSentence
                    : !recipientsAvailable
                      ? 'Email recipients could not be read. Reload before allowing Email.'
                      : 'When off, this automation still runs and executes its non-email outputs.'}
          </Typography>
          {automation.allow_email === true && blockedRecipientsSentence && (
            <Alert severity="warning" sx={{ mt: 1 }}>
              {`Email is allowed, but an Email Output with a recipient that cannot be emailed is refused whole and its run fails. ${blockedRecipientsSentence}`}
            </Alert>
          )}
          {onRetryTarget && !managed && (!pinMatchesHead || !recipientsAvailable) && (
            <Button size="small" onClick={onRetryTarget} disabled={acting || targetLoading}>Reload Function</Button>
          )}
        </>
      )}

      {/* Someone else is editing. Taking over ends their session; their unsaved
          edits (a draft on their screen) are never written. */}
      <Dialog
        open={lease.state === 'blocked' || lease.state === 'unavailable'}
        onClose={lease.takeoverBusy ? undefined : handleCancel}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>This automation is being edited</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {lease.state === 'blocked'
              ? `${lease.blockingLease?.holder_name || 'Another user'} is editing this automation (started ${leaseStartedLabel(lease.blockingLease)}).`
              : 'The edit lock for this automation could not be read.'}
            {' '}Taking over ends their editing session. Any changes they have not saved will be lost.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancel} disabled={lease.takeoverBusy}>Cancel</Button>
          <Button onClick={lease.takeOver} variant="contained" disabled={lease.takeoverBusy}>
            {lease.takeoverBusy ? 'Taking over…' : 'Take over'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={Boolean(emailReview)} onClose={busy ? undefined : () => setEmailReview(null)}>
        <DialogTitle>Allow Email Outputs on this automation?</DialogTitle>
        <DialogContent>
          <DialogContentText component="div">
            {`Every time this automation runs, Decode will send these Email Outputs of version ${emailReview?.version}:`}
            <Box component="ul" sx={{ mt: 1, mb: 1, pl: 2.5 }}>
              {(emailReview?.outputs || []).map((output) => (
                <li key={output.blockId}>
                  <strong>{output.name}</strong> &rarr; {output.recipients.map((recipient) => recipient.label).join(', ') || 'No recipients selected'}
                  {output.recipients.filter((recipient) => recipient.blocked).map((recipient) => (
                    <Alert key={recipient.id} severity="warning" sx={{ my: 1 }}>
                      {`${recipient.label}: ${recipient.blocked}`}
                    </Alert>
                  ))}
                </li>
              ))}
            </Box>
            {emailReview?.changed && (
              <Alert severity="warning" sx={{ mb: 1 }}>
                The recipients changed. Review the updated details before allowing Email.
              </Alert>
            )}
            This authorises automatic sending on every run until you publish this function again,
            change this automation, or turn it off.
          </DialogContentText>
          {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEmailReview(null)} disabled={busy}>Cancel</Button>
          <Button
            variant="contained"
            disabled={busy || blockedRecipients(emailReview).length > 0}
            onClick={handleConfirmAllowEmail}
          >
            Allow Email
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={migrateConfirmOpen} onClose={() => setMigrateConfirmOpen(false)}>
        <DialogTitle>Update this automation to version {publishedVersion}?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {`This automation was configured against an older version of this function. Switching it on runs it against version ${publishedVersion} and updates your saved filters to it.`}
            {migratePreview === null
              ? ' Checking which saved filters still apply…'
              : (resetSentence(migratePreview, true) || ' Every saved filter still applies and is kept.')}
            {' Email stays off until you allow it again.'}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setMigrateConfirmOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            disabled={migratePreview === null}
            onClick={() => { setMigrateConfirmOpen(false); handleEnable(); }}
          >
            Switch on
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={deleteConfirmOpen} onClose={() => setDeleteConfirmOpen(false)}>
        <DialogTitle>Delete {automation.name || automation.id}?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            This automation will stop running and be removed. The function it runs is not affected.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirmOpen(false)} disabled={busy}>Cancel</Button>
          <Button color="error" variant="contained" onClick={handleDelete} disabled={busy}>Delete</Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
}

export default AutomationEditor;
